import { toast } from 'sonner';
import { isGenericErrorToastSuppressed } from './toast.utils';

/**
 * Wraps sonner's `toast.error` at import time so EVERY call site
 * (513+ across pages / hooks / components — too many to migrate
 * one-by-one) honors the lifecycle-toast suppression window set by
 * the axios interceptor after a SUBSCRIPTION_SUSPENDED /
 * SUBSCRIPTION_PAST_DUE rejection.
 *
 * The interceptor fires ONE translated lifecycle toast + opens the
 * window; every subsequent `toast.error(...)` (raw sonner call OR
 * `showToast.error.*` helper) short-circuits until the window ends
 * (~1.5 s later — enough for React Query to propagate the rejection
 * through per-mutation onError handlers).
 *
 * Import from `main.tsx` BEFORE `App` so the patch is live before
 * any page-level toast fires.
 */
type ToastErrorFn = typeof toast.error;

const originalError: ToastErrorFn = toast.error.bind(toast);

const patched: ToastErrorFn = ((message, data) => {
  if (isGenericErrorToastSuppressed()) {
    // Match sonner's return contract — callers ignore this, but a
    // spec that inspects it should still get a number-ish id.
    return -1 as unknown as ReturnType<ToastErrorFn>;
  }
  return originalError(message, data);
}) as ToastErrorFn;

// Copy any static props sonner attaches (e.g. `toast.error.rich`).
Object.assign(patched, originalError);

toast.error = patched;
